"use client";
import { Button, Box, Container, Text, Group, Menu, ActionIcon, Drawer, Stack, Title, Divider } from "@mantine/core";
import Image from "next/image";
import { Link, usePathname } from "@/i18n/navigation";
import { IconMenu2, IconArrowRight } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useState, useEffect } from "react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslations, useLocale } from "next-intl";
import { trackCtaClick } from "@/lib/analytics";
import classes from "./HeaderMenu.module.css";

export function Navbar() {
  const t = useTranslations("nav");
  const locale = useLocale();
  const pathname = usePathname();
  const [opened, { open, close }] = useDisclosure(false);

  const [memberId, setMemberId] = useState<string | null>(null);
  useEffect(() => {
    const refreshMembership = () => {
      try { setMemberId(localStorage.getItem("styleClubMemberId")); } catch { setMemberId(null); }
    };
    refreshMembership();
    window.addEventListener("storage", refreshMembership);
    window.addEventListener("styleClubMemberChanged", refreshMembership);
    return () => {
      window.removeEventListener("storage", refreshMembership);
      window.removeEventListener("styleClubMemberChanged", refreshMembership);
    };
  }, []);

  const isColorAnalysis = pathname?.includes("analysis-wizard") || pathname?.includes("advanced-draping-wizard");
  if (isColorAnalysis) return null;
  const isHomePage = pathname === "/";
  const headerStyle = !isHomePage ? { position: "static" as const } : undefined;

  const navigationItems = memberId
    ? [
        { label: t("freeTools"), href: "/tools", description: t("freeToolsDesc") },
        { label: "Outfits", href: "/outfits", description: "Outfit ideas for your color season and body type" },
        { label: "My Drops", href: `/style-club/member?id=${memberId}`, description: "Your weekly outfit drops" },
        {
          label: "My Stylist",
          href: `/style-club/stylist?id=${memberId}`,
          primary: true,
          description: "Chat with your personal stylist",
        },
      ]
    : [
    { label: t("freeTools"), href: "/tools", description: t("freeToolsDesc") },
    { label: "Outfits", href: "/outfits", description: "Outfit ideas for your color season and body type" },
    { label: "Style Club", href: "/style-club", description: "Weekly outfit plans and your AI stylist" },
    { label: "Free Draping", href: "/virtual-color-draping", description: "Compare all 12 seasons on your photo" },
    ...(locale === "en" ? [
      { label: "Look Studio", href: "/virtual-makeover", description: "Hair, lipstick and blush in one mirror" },
      { label: "Blush Color Finder", href: "/what-color-blush-should-i-wear", description: "Try blush shades on your photo" },
      { label: "Lipstick Shade Finder", href: "/what-color-lipstick-should-i-wear", description: "Find your nude, red or statement shade" },
    ] : []),
    { label: "Complete Style Analysis", href: "/style-profile-bundle", description: t("completeProfileDesc"), highlight: true },
    {
      label: t("getStarted"),
      href: "/ai-color-analysis",
      primary: true,
      description: t("getStartedDesc"),
    },
  ];

  return (
    <header className={classes.header} style={headerStyle}>
      <Container size="xl" className={classes.headerInternalContainer}>
        <div className={classes.inner}>
          <Link href="/" className={classes.link} style={{ paddingInline: 0 }}>
            <Box display="flex" style={{ alignItems: "center" }}>
              <Image src="/palette.svg" alt="ColorAnalysisTech Logo" width={28} height={28} />
              <Text ml="5" size="lg" fw="500">
                ColorAnalysisTech
              </Text>
            </Box>
          </Link>

          {/* Mobile Menu Button */}
          <Group hiddenFrom="lg">
            <ActionIcon onClick={open} variant="subtle" size="lg" aria-label="Menu" style={{ color: "#475569" }}>
              <IconMenu2 />
            </ActionIcon>
          </Group>

          {/* Mobile Navigation Drawer */}
          <Drawer opened={opened} onClose={close} position="right" size="80%" withCloseButton={false}>
            <>
              <Group justify="space-between" mb="xl">
                <Box display="flex" style={{ alignItems: "center" }}>
                  <Image src="/palette.svg" alt="ColorAnalysisTech Logo" width={28} height={28} />
                  <Text ml="5" size="lg" fw="500">
                    ColorAnalysisTech
                  </Text>
                </Box>
                <ActionIcon onClick={close} variant="subtle" size="lg" color="gray">
                  <IconMenu2 />
                </ActionIcon>
              </Group>

              <Stack gap="0">
                {navigationItems.map((item, index) => (
                  <Box key={item.href}>
                    {index > 0 && <Divider my="md" color="#f1f5f9" />}
                    <Link
                      href={item.href}
                      onClick={() => {
                        close();
                        if (item.primary) trackCtaClick("nav_mobile_get_my_colors");
                      }}
                      style={{ textDecoration: "none" }}
                    >
                      <Box
                        p="md"
                        style={{
                          borderRadius: "16px",
                          background: item.primary
                            ? "#1e293b"
                            : (item as any).highlight
                              ? "linear-gradient(135deg, #fdf2f8 0%, #fff 100%)"
                              : "transparent",
                          border: item.primary
                            ? "none"
                            : (item as any).highlight
                              ? "1px solid #fce7f3"
                              : "1px solid #e2e8f0",
                        }}
                      >
                        <Text size="lg" fw={500} mb={4} style={{ color: item.primary ? "white" : "#1e293b" }}>
                          {item.label}
                        </Text>
                        <Text size="sm" style={{ color: item.primary ? "rgba(255,255,255,0.8)" : "#64748b" }}>
                          {item.description}
                        </Text>
                      </Box>
                    </Link>
                  </Box>
                ))}

                {/* Language Switcher in Mobile Menu */}
                <Divider my="md" color="#f1f5f9" />
                <Box p="md">
                  <Text size="sm" fw={500} mb="xs" style={{ color: "#64748b" }}>
                    {t("language")}
                  </Text>
                  <LanguageSwitcher />
                </Box>
              </Stack>
            </>
          </Drawer>

          {/* Desktop Navigation */}
          <Group visibleFrom="lg">
            {/* keepMounted: dropdown links must be in the SSR HTML for SEO */}
            <Menu trigger="hover" openDelay={80} closeDelay={150} keepMounted position="bottom-start" radius="md" shadow="md">
              <Menu.Target>
                <Button
                  variant="subtle"
                  radius="xl"
                  size="sm"
                  style={{ color: "#475569" }}
                >
                  {t("freeTools")}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {[
                  { label: "Color Analysis Quiz", href: "/color-analysis-quiz" },
                  { label: "Undertone Quiz", href: "/undertone-quiz" },
                  { label: "Gold or Silver Jewelry Test", href: "/gold-or-silver-jewelry-test" },
                  { label: "Skin Tone Test", href: "/skin-tone-test" },
                  { label: "Free Color Draping", href: "/virtual-color-draping" },
                  { label: "Kibbe Body Type Test", href: "/kibbe-body-type-test" },
                  { label: "AI Hair Color Changer", href: "/ai-hair-color-try-on" },
                  ...(locale === "en" ? [
      { label: "Look Studio", href: "/virtual-makeover", description: "Hair, lipstick and blush in one mirror" },
                    { label: "Blush Color Finder", href: "/what-color-blush-should-i-wear" },
                    { label: "Lipstick Shade Finder", href: "/what-color-lipstick-should-i-wear" },
                  ] : []),
                  { label: "All Free Tools", href: "/tools" },
                ].map((item) => (
                  <Menu.Item key={item.href} component={Link} href={item.href}>
                    {item.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
            <Link href="/outfits" passHref>
              <Button
                variant="subtle"
                radius="xl"
                size="sm"
                style={{ color: "#475569" }}
              >
                Outfits
              </Button>
            </Link>
            <Link href={memberId ? `/style-club/member?id=${memberId}` : "/style-club"} passHref>
              <Button
                variant="subtle"
                radius="xl"
                size="sm"
                style={{ color: "#475569" }}
              >
                {memberId ? "My Drops" : "Style Club"}
              </Button>
            </Link>
            {!memberId && <Link href="/style-profile-bundle" passHref>
              <Button
                variant="subtle"
                radius="xl"
                size="sm"
                style={{ color: "#ec4899" }}
              >
                {t("completeProfile")}
              </Button>
            </Link>}
            <LanguageSwitcher />
            <Link href={memberId ? `/style-club/stylist?id=${memberId}` : "/ai-color-analysis"} passHref>
              <Button
                name="get-started"
                radius="xl"
                size="sm"
                onClick={() => trackCtaClick(memberId ? "nav_my_stylist" : "nav_get_my_colors")}
                rightSection={<IconArrowRight size={16} />}
                style={{
                  background: "#1e293b",
                  color: "white",
                }}
              >
                {memberId ? "My Stylist" : t("getMyColors")}
              </Button>
            </Link>
          </Group>
        </div>
      </Container>
    </header>
  );
}
